import type { ClientWorkspaceSummaryViewDto } from '@/types/company'

/** The fields both helpers here read off a workspace. */
type WorkspaceChoice = Pick<ClientWorkspaceSummaryViewDto, 'Id' | 'Status'>

/** What the shell should do to bring the current workspace back in line with the URL. */
export type WorkspaceSelection =
  | { action: 'keep' }
  | { action: 'select'; workspaceId: string }
  | { action: 'redirect'; workspaceId: string }

interface WorkspaceSelectionInput {
  /** The workspaces of the company in the URL, as CompanyProvider has them. */
  workspaces: WorkspaceChoice[] | null | undefined
  /** The `/workspace/:workspaceId` segment of the page being rendered. */
  routeWorkspaceId: string | undefined
  /** What CompanyProvider currently holds. */
  currentWorkspaceId: string | null | undefined
}

function isActive(workspace: WorkspaceChoice): boolean {
  // Anything but a status string is read as active rather than thrown on. This helper runs on every
  // path that lands a user somewhere — sign-in included — so a status the dashboard cannot parse has
  // to degrade into "usable workspace", never into a crash on the way in. Coercing with String()
  // instead would turn an unparseable value into a workspace treated as inactive, which is the
  // wrong direction: it would route people away from a workspace that is very likely fine.
  if (typeof workspace.Status !== 'string') {
    return true
  }
  return workspace.Status.toLowerCase() === 'active'
}

/**
 * Where to land someone who has not named a workspace they can reach, or undefined when the company
 * has none loaded.
 *
 * An active workspace beats the company's first, which may be Inactive or Archived. The API sets
 * this on every summary it returns — `UserProfileService` projects `Status` off a non-nullable
 * `WorkspaceStatus` (Active | Inactive | Archived), whose identifiers match their descriptions — so
 * the value is always there to read. A workspace with no status still counts as active, because the
 * DTO declares the field optional and skipping such a record could send someone past the only
 * workspace they have.
 *
 * Every entry point that lands a user in a workspace shares this: RootRedirect, CompanyBootstrap,
 * and the shell's fallback below. Two of them answering "where is the default?" differently is the
 * same split this module exists to close, one level up.
 */
export function defaultWorkspace<T extends WorkspaceChoice>(workspaces: T[] | null | undefined): T | undefined {
  if (!workspaces || workspaces.length === 0) {
    return undefined
  }
  return workspaces.find(isActive) ?? workspaces[0]
}

/**
 * Reconciles the workspace held in CompanyProvider against the one named in the URL (ORB-651).
 *
 * The URL is the source of truth, and it has to be, because the provider has no selection at all on
 * a fresh page load and falls back to the company's first workspace. Anything that only asks "is the
 * current selection valid?" accepts that fallback — so after a refresh deep in workspace two, the
 * page renders workspace two while the selectors, the left nav's capability lookup, the Webster chat
 * scope and the export filenames all sit on workspace one. Nothing errors; the two halves of the
 * screen simply disagree. Browser Back between workspaces desyncs the same way.
 *
 * `redirect` is the one case the URL does not win: it names a workspace this user cannot reach, so
 * the caller sends them to their default one instead.
 */
export function resolveWorkspaceSelection({
  workspaces,
  routeWorkspaceId,
  currentWorkspaceId,
}: WorkspaceSelectionInput): WorkspaceSelection {
  if (routeWorkspaceId && workspaces?.some(w => w.Id === routeWorkspaceId)) {
    return currentWorkspaceId === routeWorkspaceId
      ? { action: 'keep' }
      : { action: 'select', workspaceId: routeWorkspaceId }
  }

  // No workspaces yet means the company is still loading, not that the URL is wrong — redirecting on
  // that gap would bounce someone out of the workspace they deep-linked to before it could resolve.
  const fallback = defaultWorkspace(workspaces)
  return fallback ? { action: 'redirect', workspaceId: fallback.Id } : { action: 'keep' }
}
